import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import DropdownIcon from 'assets/icons/Dropdown';
import { ChevronRight } from 'assets/icons/ChevronRight';
import { KEY_BOARD, OVERLAY_Z_INDEX_CLASS } from 'constants/general';
import BaseButton from './BaseButton';
import { t } from 'services/i18n.service';

type SelectSize = 'xs' | 'sm' | 'md' | 'lg';
type SelectVariant = 'filled' | 'outlined' | 'ghost' | 'brand' | 'muted';

export interface SelectOption {
  label: string;
  value: string | number;
  subtitle?: string;
  children?: SelectOption[];
}

interface SelectProps {
  options: SelectOption[];
  value?: string | number;
  onChange?: (value: string) => void;
  placeholder?: string;
  size?: SelectSize;
  variant?: SelectVariant;
  className?: string;
  isFullWidth?: boolean;
  isDisabled?: boolean;
  minWidth?: string | number;
  maxWidth?: string | number;
  dropdownWidth?: string | number;
  onClose?: () => void;
  defaultOpen?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  btnClassName?: string;
  endAdornment?: React.ReactNode;
  /** When true, always show the placeholder row so users can clear the selection. */
  isClearable?: boolean;
}

const sizeClasses: Record<SelectSize, string> = {
  xs: '~px-2/2.5 ~py-0/0.5 ~text-[0.625rem]/xs',
  sm: '~px-2.5/3 ~py-0.5/1 ~text-xs/[0.8rem]',
  md: '~px-3/4 ~py-1/1.5 ~text-sm/base',
  lg: '~px-4/5 ~py-1.5/2 ~text-base/lg'
};

const optionSizeClasses: Record<SelectSize, string> = {
  xs: '~text-[0.5rem]/[0.625rem] ~py-0.5/1 ~px-2/2.5',
  sm: '~text-[0.625rem]/xs ~py-1/1.5 ~px-2.5/3',
  md: '~text-xs/sm ~py-1.5/2 ~px-3/4',
  lg: '~text-sm/base ~py-2/2.5 ~px-4/5'
};

const SELECT_DROPDOWN_SURFACE =
  'bg-white dark:bg-secondary-dark-200 text-primary-900 dark:text-secondary-dark-900 border border-secondary-400/20 dark:border-secondary-dark-500';

const OPTION_INTERACTIVE =
  'hover:bg-primary-50 dark:hover:bg-secondary-dark-400 focus:bg-primary-50 dark:focus:bg-secondary-dark-400';

const OPTION_SELECTED =
  'bg-primary-50 dark:bg-secondary-dark-400 font-medium text-primary-900 dark:text-secondary-dark-900';

const variantClasses: Record<SelectVariant, string> = {
  filled:
    '!bg-white !text-primary-900 dark:!bg-secondary-dark-200 dark:!text-secondary-dark-900 !border-0 shadow-sm',
  outlined:
    '!bg-transparent !text-primary-900 dark:!bg-transparent dark:!text-secondary-dark-900 !border !border-secondary-400/60 dark:!border-secondary-dark-500',
  ghost:
    '!bg-transparent !text-primary-800 dark:!bg-transparent dark:!text-secondary-dark-800 !border-0',
  brand:
    '!bg-primary-50 !text-primary-900 dark:!bg-primary-500 dark:!text-secondary-100 !border-0 shadow-md',
  muted:
    '!bg-secondary-50 !text-primary-900 dark:!bg-secondary-dark-300 dark:!text-secondary-dark-900 !border-0 shadow-sm'
};

const dropdownVariantClasses: Record<SelectVariant, string> = {
  filled: SELECT_DROPDOWN_SURFACE,
  outlined: SELECT_DROPDOWN_SURFACE,
  ghost: SELECT_DROPDOWN_SURFACE,
  brand: SELECT_DROPDOWN_SURFACE,
  muted:
    'bg-secondary-50 dark:bg-secondary-dark-300 text-primary-900 dark:text-secondary-dark-900 border border-secondary-400/20 dark:border-secondary-dark-500'
};

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  placement: 'top' | 'bottom';
}

const Select = ({
  options,
  value,
  onChange,
  placeholder = t('settings_page.select_an_option'),
  size = 'md',
  variant = 'filled',
  className = '',
  isFullWidth = true,
  isDisabled = false,
  minWidth = '120px',
  maxWidth,
  dropdownWidth,
  onClose,
  defaultOpen = false,
  searchable = false,
  searchPlaceholder = t('settings_page.search'),
  btnClassName,
  endAdornment,
  isClearable = false
}: SelectProps) => {
  const responsiveClass = isFullWidth ? 'w-full sm:w-auto' : 'inline-block';
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
    placement: 'bottom'
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string | number>>(
    new Set()
  );

  const findSelectedOption = useCallback(
    (
      opts: SelectOption[],
      searchValue: string | number
    ): { option: SelectOption; parent?: SelectOption } | null => {
      for (const opt of opts) {
        if (String(opt.value) === String(searchValue)) {
          return { option: opt };
        }
        if (opt.children) {
          const found = findSelectedOption(opt.children, searchValue);
          if (found) {
            return { option: found.option, parent: opt };
          }
        }
      }
      return null;
    },
    []
  );

  const selectedResult = useMemo(
    () => (value ? findSelectedOption(options, value) : null),
    [value, options, findSelectedOption]
  );

  const displayValue = useMemo(() => {
    if (!selectedResult) return placeholder;
    const { option, parent } = selectedResult;
    if (parent) {
      return `${parent.label} > ${option.label}`;
    }
    return option.label;
  }, [selectedResult, placeholder]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase().trim();
    const filterOptions = (opts: SelectOption[]): SelectOption[] => {
      return opts.reduce<SelectOption[]>((acc, opt) => {
        const labelMatches = opt.label.toLowerCase().includes(query);
        if (opt.children) {
          const filteredChildren = filterOptions(opt.children);
          if (labelMatches || filteredChildren.length > 0) {
            acc.push({
              ...opt,
              children:
                filteredChildren.length > 0 ? filteredChildren : opt.children
            });
          }
        } else if (labelMatches) {
          acc.push(opt);
        }
        return acc;
      }, []);
    };
    return filterOptions(options);
  }, [options, searchQuery, searchable]);

  // Calculate dropdown position
  const calculatePosition = useCallback((): DropdownPosition => {
    if (!buttonRef.current) {
      return { top: 0, left: 0, width: 0, placement: 'bottom' };
    }

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    // Get actual dropdown height if available, otherwise estimate
    let actualDropdownHeight = 0;
    if (dropdownRef.current) {
      actualDropdownHeight = dropdownRef.current.getBoundingClientRect().height;
    }

    const countAllOptions = (opts: SelectOption[]): number => {
      let count = opts.length;
      opts.forEach((opt) => {
        if (opt.children) {
          count += countAllOptions(opt.children);
        }
      });
      return count;
    };

    const totalOptions = countAllOptions(options);
    const estimatedDropdownHeight = Math.min(totalOptions * 40 + 8, 240);
    const dropdownHeight = actualDropdownHeight || estimatedDropdownHeight;

    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // Determine vertical placement
    const shouldPlaceAbove =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    const placement: 'top' | 'bottom' = shouldPlaceAbove ? 'top' : 'bottom';

    // Calculate top position
    let top: number;
    if (placement === 'bottom') {
      top = buttonRect.bottom + scrollY + 4; // 4px gap below button
    } else {
      // Position dropdown so its bottom edge is 4px above button's top edge
      top = buttonRect.top + scrollY - dropdownHeight - 4; // 4px gap above button
    }

    // Calculate left position (align with button, but adjust if too close to edges)
    const padding = 8; // Minimum distance from viewport edges
    let left = buttonRect.left + scrollX;

    // Adjust if dropdown would overflow on the right
    if (left + buttonRect.width > viewportWidth - padding) {
      left = viewportWidth - buttonRect.width - padding + scrollX;
    }

    // Adjust if dropdown would overflow on the left
    if (left < padding + scrollX) {
      left = padding + scrollX;
    }

    return {
      top,
      left,
      width: buttonRect.width,
      placement
    };
  }, [options]);

  // Update position when opening or window resizes
  useEffect(() => {
    if (isOpen) {
      // Initial position calculation
      const newPosition = calculatePosition();
      setPosition(newPosition);

      // Recalculate position after dropdown is rendered to get actual height
      const timeoutId = setTimeout(() => {
        const updatedPosition = calculatePosition();
        setPosition(updatedPosition);
      }, 0);

      const handleResize = () => {
        const updatedPosition = calculatePosition();
        setPosition(updatedPosition);
      };

      const handleScroll = () => {
        const updatedPosition = calculatePosition();
        setPosition(updatedPosition);
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen, calculatePosition]);

  const handleToggle = (event?: React.MouseEvent | React.KeyboardEvent) => {
    event?.stopPropagation();
    if (!isDisabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleSelect = (optionValue: string | number) => {
    onChange?.(String(optionValue));
    setIsOpen(false);
    setSearchQuery('');
    onClose?.();
  };

  const handleToggleExpand = (optionValue: string | number) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(optionValue)) {
        newSet.delete(optionValue);
      } else {
        newSet.add(optionValue);
      }
      return newSet;
    });
  };

  const isExpanded = (optionValue: string | number) => {
    return expandedItems.has(optionValue);
  };

  const hasChildren = (option: SelectOption) => {
    return option.children && option.children.length > 0;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return;

    if (
      event.key === KEY_BOARD.ENTER.KEY ||
      event.key === KEY_BOARD.SPACE.KEY
    ) {
      event.preventDefault();
      handleToggle(event);
    } else if (event.key === KEY_BOARD.ESCAPE.KEY) {
      setIsOpen(false);
      onClose?.();
    } else if (event.key === KEY_BOARD.ARROW_DOWN.KEY) {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    optionValue: string | number,
    hasChildren: boolean
  ) => {
    if (event.key === 'ArrowRight' && hasChildren) {
      event.preventDefault();
      if (!isExpanded(optionValue)) {
        handleToggleExpand(optionValue);
      }
    } else if (event.key === 'ArrowLeft' && hasChildren) {
      event.preventDefault();
      if (isExpanded(optionValue)) {
        handleToggleExpand(optionValue);
      }
    } else if (
      event.key === KEY_BOARD.ENTER.KEY ||
      event.key === KEY_BOARD.SPACE.KEY
    ) {
      event.preventDefault();
      if (hasChildren) {
        handleToggleExpand(optionValue);
      } else {
        handleSelect(optionValue);
      }
    } else if (event.key === KEY_BOARD.ESCAPE.KEY) {
      setIsOpen(false);
      onClose?.();
      buttonRef.current?.focus();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
        onClose?.();
      }
    };

    // Use capture phase to catch clicks before they bubble
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const getWidthValue = (width?: string | number) => {
    if (!width) return undefined;
    return typeof width === 'number' ? `${width}px` : width;
  };

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      role='listbox'
      className={`fixed ${OVERLAY_Z_INDEX_CLASS.POPOVER} max-h-60 overflow-auto scrollbar-thin-custom rounded-md shadow-md md:shadow-lg ${dropdownVariantClasses[variant]}`}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: dropdownWidth
          ? getWidthValue(dropdownWidth)
          : `${position.width}px`,
        minWidth: getWidthValue(minWidth),
        maxWidth: getWidthValue(maxWidth),
        transformOrigin:
          position.placement === 'top' ? 'bottom center' : 'top center'
      }}>
      {searchable && (
        <div className='p-2 border-b border-secondary-400/20 dark:border-secondary-dark-500 sticky top-0 bg-white dark:bg-secondary-dark-200'>
          <input
            ref={searchInputRef}
            type='text'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className='w-full px-2 py-1 ~text-[0.625rem]/xs border border-secondary-400/40 dark:border-secondary-dark-500 rounded bg-white dark:bg-secondary-dark-300 text-primary-900 dark:text-secondary-dark-900 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-dark-500'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <div className='py-1'>
        {placeholder && !searchQuery && (isClearable || !value) && (
          <div
            role='option'
            aria-selected={!value}
            className={`${optionSizeClasses[size]} cursor-pointer ${OPTION_INTERACTIVE} ${
              !value
                ? 'font-semibold text-primary-900 dark:text-secondary-dark-900'
                : 'text-primary-700 dark:text-secondary-dark-700'
            }`}
            onClick={() => handleSelect('')}
            onKeyDown={(e) => handleOptionKeyDown(e, '', false)}
            tabIndex={0}>
            {placeholder}
          </div>
        )}
        {filteredOptions.length === 0 && searchQuery && (
          <div
            className={`${optionSizeClasses[size]} text-primary-700 dark:text-secondary-dark-700 text-center`}>
            {t('no_results_found')}
          </div>
        )}
        {filteredOptions.map((opt) => {
          const renderOption = (option: SelectOption, isChild = false) => {
            const optionHasChildren = hasChildren(option);
            const optionIsExpanded = isExpanded(option.value);
            const optionIsSelected =
              String(option.value) === String(value) ||
              (option.children &&
                option.children.some(
                  (child) => String(child.value) === String(value)
                ));

            return (
              <div key={String(option.value)}>
                <div
                  role={optionHasChildren ? 'group' : 'option'}
                  aria-selected={!optionHasChildren && optionIsSelected}
                  aria-expanded={
                    optionHasChildren ? optionIsExpanded : undefined
                  }
                  className={`${optionSizeClasses[size]} ${
                    optionHasChildren
                      ? 'font-medium cursor-pointer'
                      : 'cursor-pointer'
                  } ${OPTION_INTERACTIVE} outline-none ${
                    !optionHasChildren && optionIsSelected
                      ? OPTION_SELECTED
                      : ''
                  } ${
                    option.subtitle
                      ? 'flex items-start gap-3'
                      : 'flex items-center gap-2'
                  } ${isChild ? '~pl-2/4' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (optionHasChildren) {
                      handleToggleExpand(option.value);
                    } else {
                      handleSelect(option.value);
                    }
                  }}
                  onKeyDown={(e) =>
                    handleOptionKeyDown(e, option.value, !!optionHasChildren)
                  }
                  tabIndex={0}>
                  {optionHasChildren && (
                    <div className='flex-shrink-0'>
                      <ChevronRight
                        styles={`~w-3/3.5 h-auto transition-transform duration-200 ${
                          optionIsExpanded ? 'rotate-90' : ''
                        }`}
                      />
                    </div>
                  )}
                  {option.subtitle ? (
                    <>
                      <div className='flex-shrink-0 ~mt-1/1.5'>
                        <div
                          className={`~w-3/3.5 ~h-3/3.5 rounded-full border-2 flex items-center justify-center ${
                            !optionHasChildren && optionIsSelected
                              ? 'border-primary-500 bg-primary-500 dark:border-primary-500 dark:bg-primary-500'
                              : 'border-secondary-400 dark:border-secondary-dark-600 bg-transparent'
                          }`}>
                          {!optionHasChildren && optionIsSelected && (
                            <div className='~w-1.5/2 ~h-1.5/2 rounded-full bg-white' />
                          )}
                        </div>
                      </div>
                      <div className='flex-1'>
                        <div className='~text-xs/sm font-medium text-primary-900 dark:text-secondary-dark-900 my-auto'>
                          {option.label}
                        </div>
                        {option.subtitle && (
                          <div className='~text-[0.6rem]/xs text-primary-700 dark:text-secondary-dark-700 mt-0.5'>
                            {option.subtitle}
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <span className='flex-1'>{option.label}</span>
                  )}
                </div>
                {optionHasChildren && optionIsExpanded && option.children && (
                  <div className='~ml-4/8 border-l-2 border-secondary-400/30 dark:border-secondary-dark-500'>
                    {option.children.map((child) => renderOption(child, true))}
                  </div>
                )}
              </div>
            );
          };

          return renderOption(opt);
        })}
      </div>
    </div>
  ) : null;

  const mlClasses: Record<SelectSize, string> = {
    xs: 'ml-0.5',
    sm: '~ml-0.5/1',
    md: '~ml-0.5/1.5',
    lg: '~ml-0.5/2'
  };

  return (
    <>
      <div
        className={`${responsiveClass} ${className} relative inline-flex items-center`}>
        <BaseButton
          ref={buttonRef}
          variant='icon'
          disabled={isDisabled}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          className={`${sizeClasses[size]} ${variantClasses[variant]} ${btnClassName} rounded-md appearance-none flex items-center justify-between truncate outline-none focus:outline-none focus:ring focus:ring-primary-300 dark:focus:ring-primary-900 ${
            isDisabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-pointer hover:opacity-90'
          }`}
          aria-haspopup='listbox'
          aria-expanded={isOpen}
          aria-disabled={isDisabled}
          size={size}
          additionalStyles='hover:scale-100 w-full'>
          <span
            className={`flex-1 text-left truncate min-w-0 ${
              isFullWidth
                ? ''
                : '~sm:~min-w-[4rem]/[6rem] ~sm:~max-w-[10rem]/[12rem]'
            }`}>
            {displayValue}
          </span>
          <span
            aria-hidden
            className={`pointer-events-none ${mlClasses[size]} flex-shrink-0 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : 'rotate-0'
            }`}>
            <DropdownIcon styles='~w-4/5 h-auto' />
          </span>
        </BaseButton>
        {endAdornment ? (
          <span className='flex-shrink-0 inline-flex items-center'>
            {endAdornment}
          </span>
        ) : null}
      </div>
      {createPortal(dropdownContent, document.body)}
    </>
  );
};

export default Select;
